import { BookingType, PricingType, Space } from "@/data";
import { AvailableDay } from "@/offering/api";
import pluralize from "pluralize";
import React, { useCallback, useEffect } from "react";
import { useState } from "react";
import { colors } from "../colors";
import { TimeSlot } from "../date-time-picker/checkout_datetime_picker";
import {
  DailyValue,
  DateTimePicker,
  DateTimeValue,
  HourlyValue,
  isHourlyValue,
} from "../date-time-picker/date_time_picker";
import { PickingRange } from "../date_range_picker/date_range_picker";

interface SelectWhenToHostProps {
  availableDays?: AvailableDay[];
  timeslots: TimeSlot[];
  onChangeMonth: (startDate: Date, endDate: Date) => void;
  onChangeDateTime: (value?: DateTimeValue) => void;
  value?: DateTimeValue;
  space?: Space | null;
  pickingRange: PickingRange;
  spaceLayoutId?: string;
}

export function SelectWhenToHost({
  availableDays,
  timeslots,
  onChangeMonth,
  onChangeDateTime,
  value,
  space,
  pickingRange,
  spaceLayoutId,
}: SelectWhenToHostProps) {
  const minDurationHours = space?.bookingPolicy.minDuration;

  const [switchValue, setSwitchValue] = useState(
    isHourlyValue(value) || !value ? BookingType.Hourly : BookingType.Daily
  );
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  let hasDailyPrice = false;
  let hasHourlyPrice = false;
  space?.pricings.forEach((p) => {
    if (p.type === PricingType.Daily) {
      hasDailyPrice = true;
    } else if (p.type === PricingType.Hourly) {
      hasHourlyPrice = true;
    }
  });

  const handleHourlyChange = useCallback(
    (value: HourlyValue) => {
      if (value.date) {
        onChangeDateTime({ date: value.date });
        setLocalValue({ date: value.date });
      }
      if (value.startTime) {
        setLocalValue({
          ...localValue,
          startTime: value.startTime,
          endTime: value.endTime,
        });
        if (value.endTime) {
          onChangeDateTime({
            date: isHourlyValue(localValue) ? localValue.date : undefined,
            startTime: value.startTime,
            endTime: value.endTime,
          });
        }
      }
    },
    [localValue, onChangeDateTime]
  );

  const handleDailyChange = useCallback(
    (value: DailyValue) => {
      if (value.startDate) {
        onChangeDateTime({ date: value.startDate });
      }

      if (value.endDate) {
        onChangeDateTime({
          startDate: value.startDate,
          endDate: value.endDate,
        });
      }
    },
    [onChangeDateTime]
  );

  const handleSwitch = (value: BookingType) => {
    setSwitchValue(value);
    if (isHourlyValue(localValue)) {
      onChangeDateTime({ date: undefined });
    } else {
      onChangeDateTime({ startDate: undefined, endDate: undefined });
    }
  };

  const baseProps = {
    switchValue,
    space,
    timeslots,
    availableDays,
    onChangeMonth: onChangeMonth,
    onSwitch: handleSwitch,
    hideSwitch: !(hasDailyPrice && hasHourlyPrice),
    spaceLayoutId,
  };

  const calendarComponent: JSX.Element =
    switchValue === BookingType.Daily ? (
      <DateTimePicker<DailyValue>
        {...baseProps}
        value={!isHourlyValue(value) ? value : undefined}
        onChange={handleDailyChange}
        pickingRange={pickingRange}
      />
    ) : (
      <DateTimePicker<HourlyValue>
        {...baseProps}
        value={isHourlyValue(localValue) ? localValue : undefined}
        onChange={handleHourlyChange}
        pickingRange={PickingRange.OneDate}
      />
    );

  return (
    <div data-testid="event-space-select-when-to-host">
      {calendarComponent}
      {minDurationHours && (
        <div
          className="text-center"
          style={{ color: colors.blackMinus40, fontSize: 14 }}
        >
          Minimum duration: {minDurationHours}{" "}
          {pluralize("hour", minDurationHours)}
        </div>
      )}
    </div>
  );
}
